import { Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core";
import { WidgetSourceService } from "@main/reporting.v3/api/widget-source.service";
import {
    getWidgetSourceTypeLabel,
    IWidgetSource,
    WidgetAccessLevel,
    WidgetSourceType
} from "@main/reporting.v3/core/reporting-models";
import {
    getLayoutTypeName,
    IDashboardBuild,
    IDashboardIM1DataSet,
    IDashboardView,
    IDashboardWidgetBuild,
    IDashboardWidgetView,
    LayoutType,
} from "@main/reporting.v3/dashboard/dashboard-models";
import { DashboardService } from "@main/reporting.v3/dashboard/api/dashboard.service";
import { ConfirmationService, MenuItem, MessageService, SelectItem, SelectItemGroup } from "primeng/api";
import { SecurityService } from "@bbwt/modules/security";
import { DropdownChangeEvent } from "primeng/dropdown";
import { WidgetBuilderComponent } from "@main/reporting.v3/widget/widget-builder.component";
import {
    ReportingDashboardQueriesComponent
} from "@main/reporting.v3/dashboard/components/dashboard-queries.component";
import { IHtmlViewDTO } from "@main/reporting.v3/widget.html/widget-html.models";
import { HtmlRenderingService } from "@main/reporting.v3/core/rendering/html-rendering.service";
import { DashboardAuxiliaryService } from "@main/reporting.v3/dashboard/api/dashboard-auxiliary.service";
import { CodeEditorKeyboardService } from "@controls/text-editors/services/code-editor-keyboard.service";
import { DashboardQueryService } from "@main/reporting.v3/dashboard/api/dashboard-query.service";
import { createOptions as createIM1DataSetsOptions } from "../im1-data-set";
import { KeyBinding } from "@codemirror/view";
import { Extension } from "@codemirror/state";
import { Router } from "@angular/router";
import { Message } from "@bbwt/classes";
import { Menu } from "primeng/menu";
import { v4 as uuidv4 } from "uuid";
import { take } from "rxjs";
import { DashboardQueryResolverService } from "../services/dashboard-query-resolver.service";
import { dashboardQueryResolverProvider } from "../models/dashboard-query-resolver-provider.model";


@Component({
    selector: "reporting-dashboard-builder",
    templateUrl: "./dashboard-builder.component.html",
    styleUrls: ["./dashboard-builder.component.scss"],
    providers: [
        DashboardQueryResolverService,
        dashboardQueryResolverProvider,
    ]
})
export class ReportingDashboardBuilderComponent implements OnInit {
    // CSS injection settings.
    public readonly cssInjectionScopeId: string = uuidv4();

    // Widget builder settings.
    protected rowIndex: number;
    protected columnIndex: number;
    protected widgetImport: boolean;

    // Widget source settings.
    protected widgetSource: IWidgetSource;
    protected widgetSources: IWidgetSource[];
    protected widgetSourceOptions: SelectItem[] = [];
    protected widgetSourceMenuItems: MenuItem[] = [];
    protected querySourceOptions: SelectItemGroup[] = [];

    // Dashboard build.
    protected dashboard: IDashboardBuild;
    protected dashboardGrid: IDashboardWidgetBuild[][] = [];
    protected dashboardWidgets: IDashboardWidgetBuild[] = [];

    // Dashboard preview.
    protected dashboardPreview: IDashboardView;

    // Dashboard general settings.
    protected dashboardName: string;
    protected dashboardCode: string;
    protected description: string;
    protected headerHtmlCode: string;
    protected headerCssStyle: string;
    protected footerHtmlCode: string;
    protected footerCssStyle: string;
    protected isSystemDashboard: boolean;
    protected displayName: boolean = true;
    protected widgetSaveButtonTooltip: string;
    protected hasForcedPermitToLeave: boolean;

    // Drag settings.
    protected dragging: boolean = false;
    protected draggableWidget: IDashboardWidgetBuild = null;
    protected shrinkableWidget: IDashboardWidgetBuild = null;

    // Layout settings.
    protected dashboardStyles: string;
    protected dashboardStylesBackup: string;
    protected editStylesDialogVisible: boolean;

    protected widgetsMargin: number = 0;
    protected widgetsMarginUnit: string = "px";

    protected widgetsPadding: number = 0;
    protected widgetsPaddingUnit: string = "px";

    protected layoutOptions: SelectItem[];
    protected styleEditorKeyboard: Extension;
    protected styleEditorExtensions: Extension[];
    protected layoutStyle: LayoutType = LayoutType.Dividers;

    protected readonly LayoutType: typeof LayoutType = LayoutType;
    protected readonly WidgetAccessLevel: typeof WidgetAccessLevel = WidgetAccessLevel;
    protected readonly getLayoutTypeName: (code: LayoutType) => string = getLayoutTypeName;

    // NHS Settings
    protected dashboardIM1DataSetsOptions: SelectItem[] = [];
    protected dashboardIM1DataSets: IDashboardIM1DataSet[];
    protected iM1DailyAutoRefresh: boolean;
    protected showIM1RefreshButton: boolean;

    protected loadingMessageHtmlCode: string;
    protected loadingMessageCssRules: string;

    protected errorHandlingHtmlCode: string;
    protected errorHandlingCssRules: string;

    protected toolbar: string = "all";

    protected loadingMessageHtmlPreview: IHtmlViewDTO;
    protected errorHandlingHtmlPreview: IHtmlViewDTO;

    private _dashboardId: string;

    @ViewChild(Menu, { static: true }) private menu: Menu;
    @ViewChild(WidgetBuilderComponent) private widgetBuilder: WidgetBuilderComponent;
    @ViewChild(ReportingDashboardQueriesComponent) private queriesComponent: ReportingDashboardQueriesComponent;

    constructor(private readonly dashboardService: DashboardService,
                private readonly dashboardQueryService: DashboardQueryService,
                private dashboardQueryResolverService: DashboardQueryResolverService,
                private readonly dashboardAuxiliaryService: DashboardAuxiliaryService,
                private readonly keyboardService: CodeEditorKeyboardService,
                private readonly htmlRenderingService: HtmlRenderingService,
                private readonly confirmationService: ConfirmationService,
                private readonly widgetService: WidgetSourceService,
                private readonly securityService: SecurityService,
                private readonly messageService: MessageService,
                private readonly elementRef: ElementRef,
                private readonly router: Router) {
    }

    @Input() set dashboardId(value: string) {
        if (!value) return;

        // TODO: Temporary approach until we implement a dashboard store.
        this.dashboardQueryResolverService.dashboardId = value;

        this._dashboardId = value;

        this.refreshDashboard().then();
    }

    get dashboardId(): string {
        return this._dashboardId;
    }

    get dashboardBuild(): IDashboardBuild {
        return {
            ...this.dashboard,

            name: this.dashboardName,
            displayName: this.displayName,
            description: this.description,
            urlSlug: this.dashboardCode,

            pdfHeaderHtmlCode: this.headerHtmlCode,
            pdfHeaderCssStyle: this.headerCssStyle,
            pdfFooterHtmlCode: this.footerHtmlCode,
            pdfFooterCssStyle: this.footerCssStyle,

            widgets: this.dashboardWidgets,

            layout: this.layoutStyle,
            style: this.dashboardStyles,
            widgetsMargin: this.widgetsMargin,
            widgetsPadding: this.widgetsPadding,

            iM1DailyAutoRefresh: this.iM1DailyAutoRefresh,
            dashboardIM1DataSets: this.dashboardIM1DataSets,
            showIM1RefreshButton: this.showIM1RefreshButton,

            loadingMessageHtmlCode: this.loadingMessageHtmlCode,
            loadingMessageCssRules: this.loadingMessageCssRules,

            errorHandlingHtmlCode: this.errorHandlingHtmlCode,
            errorHandlingCssRules: this.errorHandlingCssRules
        };
    }

    // Dialog properties.
    get editWidgetDialogHeader(): string {
        if (!this.widgetSource?.widgetType) return "";

        const type: string = getWidgetSourceTypeLabel(this.widgetSource.widgetType);

        if (!this.widgetSource.id) return `New ${type}`;

        return !this.widgetSource.name
            ? `Edit ${type}`
            : `Edit ${type} • ${this.widgetSource.name}`;
    }

    get editWidgetDialogVisible(): boolean {
        return !!this.widgetSource?.widgetType && !this.widgetImport;
    }

    set editWidgetDialogVisible(value: boolean) {
        if (!value) this.clean();
    }

    get loadWidgetDialogVisible(): boolean {
        return !!this.widgetSource?.widgetType && this.widgetImport;
    }

    set loadWidgetDialogVisible(value: boolean) {
        if (!value) this.clean();
    }

    // Widget builder properties.
    get queryBuilderTabActive(): boolean {
        return !!this.widgetBuilder?.queryBuilderTabActive;
    }

    get queryBuilderDisabled(): boolean {
        return !this.widgetBuilder || !!this.widgetBuilder.queryBuilderDisabled;
    }

    get queryBuilderDirty(): boolean {
        return !!this.widgetBuilder?.queryBuilderDirty;
    }

    get widgetBuilderValid(): boolean {
        return !!this.widgetBuilder?.valid;
    }

    get widgetBuilderLoading(): boolean {
        return !!this.widgetBuilder?.loading;
    }

    get hasUnsavedChanges(): boolean {
        return !this.hasForcedPermitToLeave && this.dirty;
    }

    get dirty(): boolean {
        if (!this.dashboard.widgets?.length) return !this.dashboardWidgets?.length;

        const dashboard: IDashboardBuild = this.dashboardBuild;

        // Check if some widget has been deleted or edited (widget source id is changed on edition).
        const widgetEdited: boolean = this.dashboard.widgets.some(originalWidget =>
            !dashboard.widgets.some(widget => widget.widgetSourceId === originalWidget.widgetSourceId
                && widget.rowIndex === originalWidget.rowIndex && widget.columnIndex === originalWidget.columnIndex));
        if (widgetEdited) return true;

        // Check if some widget has been created.
        const widgetCreated: boolean = dashboard.widgets.some(widget =>
            !this.dashboard.widgets.some(originalWidget => widget.widgetSourceId === originalWidget.widgetSourceId));
        if (widgetCreated) return true;

        // Check if the IM1Sets has been changed:
        const im1SetsEdited = this.dashboardIM1DataSets.length != this.dashboard.dashboardIM1DataSets.length ||
            !!this.dashboardIM1DataSets.find(s => !this.dashboard.dashboardIM1DataSets.find(ds => ds == s));
        if (im1SetsEdited) return true;

        return this.dashboard.name !== dashboard.name
            || this.dashboard.layout !== dashboard.layout
            || this.dashboard.urlSlug !== dashboard.urlSlug
            || this.dashboard.displayName !== dashboard.displayName
            || this.dashboard.description !== dashboard.description
            || this.dashboard.widgetsMargin !== dashboard.widgetsMargin
            || this.dashboard.iM1DailyAutoRefresh !== dashboard.iM1DailyAutoRefresh
            || this.dashboard.showIM1RefreshButton !== dashboard.showIM1RefreshButton
            || this.dashboard.loadingMessageHtmlCode !== dashboard.loadingMessageHtmlCode
            || this.dashboard.loadingMessageCssRules !== dashboard.loadingMessageCssRules
            || this.dashboard.errorHandlingHtmlCode !== dashboard.errorHandlingHtmlCode
            || this.dashboard.errorHandlingCssRules !== dashboard.errorHandlingCssRules;
    }


    public ngOnInit(): void {
        this.layoutOptions = [
            { label: getLayoutTypeName(LayoutType.Cards), value: LayoutType.Cards },
            { label: getLayoutTypeName(LayoutType.Dividers), value: LayoutType.Dividers },
            { label: getLayoutTypeName(LayoutType.Plain), value: LayoutType.Plain }
        ];

        const creationCommand = (widgetType: WidgetSourceType): void => {
            this.widgetImport = false;
            this.widgetSource = { widgetType: widgetType } as IWidgetSource;
        }

        const importCommand = (): void => {
            if (!this.widgetSourceOptions?.length) {
                return this.messageService.add(Message.Info("There is no available widgets, try creating a new one."));
            }
            this.widgetImport = true;
            this.widgetSource = this.widgetSourceOptions[0].value;
        }

        this.widgetSourceMenuItems = [
            {
                label: getWidgetSourceTypeLabel("control-set"),
                icon: "pi pi-minus-circle",
                command: _ => creationCommand("control-set"),
            },
            {
                label: getWidgetSourceTypeLabel("table"),
                icon: "pi pi-table",
                command: _ => creationCommand("table"),
            },
            {
                label: getWidgetSourceTypeLabel("chart"),
                icon: "pi pi-chart-pie",
                command: _ => creationCommand("chart"),
            },
            {
                label: getWidgetSourceTypeLabel("html"),
                icon: "pi pi-code",
                command: _ => creationCommand("html"),
            },
            {
                label: "Use existing widget",
                icon: "pi pi-file-import",
                command: _ => importCommand(),
            }
        ];

        this.widgetSaveButtonTooltip = "There are changes in the query. If you save the widget these changes " +
            "will be saved automatically and if the query schema changes they may modify the settings you have set for the widget.";

        this.styleEditorKeyboard = this.keyboardService.getKeyboardExtension([
            this.getCtrlEnterKeyboardHandler(),
        ]);

        this.styleEditorExtensions = [this.styleEditorKeyboard];
    }


    // Widget builder methods.
    protected widgetBuilderSave(): void {
        if (!this.widgetBuilder) return;

        // If this widget is a draft, make the changes over the query.
        // Otherwise, create a new draft widget for them.
        this.widgetBuilder?.saveDraft().then(widgetSourceId =>
            // Get widget source from widget id (more information, as the name, are required from the widget).
            this.widgetService.get(widgetSourceId).then(widgetSource => {

                if (!this.widgetSource?.id) this.addWidget(widgetSource);
                else this.refreshWidget(widgetSource);

                this.refreshDashboardQueriesGrid();

                this.editWidgetDialogVisible = false;
            }));
    }

    protected widgetBuilderSaveQuery(): void {
        if (!this.widgetBuilder) return;

        // Internally, widget builder saves the query as draft, to be able to cancel modifications.
        if (this.widgetBuilder.saveQuery) this.widgetBuilder.saveQuery().then();
    }

    protected widgetBuilderCancel(): void {
        if (!this.widgetBuilder) return;

        const widgetSourceId: string = this.widgetBuilder?.widgetSourceId;
        // If widget source is not being used by any dashboard widget and if it's a draft, delete it.
        if (!widgetSourceId || !this.dashboardWidgets.some(widget => widget.widgetSourceId === widgetSourceId)) {
            this.widgetBuilder.cancelDraft().then();
        }

        this.editWidgetDialogVisible = false;
    }

    // Refreshing methods.
    private async refreshDashboard(): Promise<void> {
        // Remove old widget drafts that could remain due precedent editions of this dashboard.
        await this.dashboardService.removeDashboardWidgetDrafts(this.dashboardId);

        // Remove old query drafts that could remain due precedent editions of this dashboard.
        await this.dashboardQueryService.removeDashboardQueryDrafts(this.dashboardId);

        // Get the view of this dashboard.
        this.dashboard = await this.dashboardService.getBuild(this.dashboardId);
        // Clone all dashboard widgets to avoid reference issues.
        this.dashboardWidgets = [];
        this.dashboard.widgets?.forEach(widget => this.dashboardWidgets.push({ ...widget }));

        // Get all widgets that are visible to this user.
        this.widgetSources = await this.widgetService.getAll();
        // Add widgets currently in the dashboard, avoiding repetitions.
        this.dashboardWidgets.forEach(widget => {
            if (this.widgetSources.some(source => source.id === widget.widgetSource.id)) return;
            this.widgetSources.push(widget.widgetSource);
        });

        this.refreshWidgetSourceSettings();
        this.refreshDashboardQueriesGrid();
        this.refreshDashboardSettings();
        this.refreshDashboardStyles();
        this.refreshDashboardGrid();
        this.refreshNHSSettings();
    }

    private refreshDashboardSettings(): void {
        if (!this.dashboard) return;

        this.dashboardName = this.dashboard.name;
        this.displayName = this.dashboard.displayName;
        this.description = this.dashboard.description;
        this.headerHtmlCode = this.dashboard.pdfHeaderHtmlCode;
        this.headerCssStyle = this.dashboard.pdfHeaderCssStyle;
        this.footerHtmlCode = this.dashboard.pdfFooterHtmlCode;
        this.footerCssStyle = this.dashboard.pdfFooterCssStyle;
        this.dashboardCode = this.dashboard.urlSlug;

        this.widgetsMargin = this.dashboard.widgetsMargin ?? 0;
        this.widgetsPadding = this.dashboard.widgetsPadding ?? 0;
        this.layoutStyle = this.dashboard.layout ?? LayoutType.Dividers;
        this.dashboardStyles = this.dashboard.style ?? this.getDefaultStyles();

        this.replaceCssPropertyValue("margin", this.widgetsMargin, this.widgetsMarginUnit);
        this.replaceCssPropertyValue("padding", this.widgetsPadding, this.widgetsPaddingUnit);
    }

    protected refreshNHSSettings(): void {
        if (!this.dashboard) return;

        this.dashboardIM1DataSetsOptions = createIM1DataSetsOptions()
            .map(si => <SelectItem>{
                ...si,
                value: <IDashboardIM1DataSet>{
                    dashboardId: this.dashboard.id,
                    iM1DataSet: si.value
                }
            });
        this.iM1DailyAutoRefresh = this.dashboard.iM1DailyAutoRefresh;
        this.showIM1RefreshButton = this.dashboard.showIM1RefreshButton;
        this.dashboardIM1DataSets = [...this.dashboard.dashboardIM1DataSets];

        this.loadingMessageHtmlCode = this.dashboard.loadingMessageHtmlCode?.trim() || "Updating NHS data, please wait…";
        this.loadingMessageCssRules = this.dashboard.loadingMessageCssRules;
        this.errorHandlingHtmlCode = this.dashboard.errorHandlingHtmlCode;
        this.errorHandlingCssRules = this.dashboard.errorHandlingCssRules;
    }

    private refreshDashboardGrid(): void {
        // Get upperbound of the amount of rows.
        const rows: number = !!this.dashboardWidgets?.length ?
            Math.max(...this.dashboardWidgets.map(widget => widget.rowIndex)) + 1 : 0;

        if (!rows) return;

        // Get a dashboard grid row.
        const getDashboardGridRow = (index: number): IDashboardWidgetBuild[] =>
            this.dashboardWidgets
                .filter(widget => widget?.rowIndex === index)
                .sort((first, second) => first.columnIndex - second.columnIndex)

        // Declare a dashboard with the corresponding amount of rows.
        this.dashboardGrid = Array(rows + 1).fill(null)
            // Get dashboard widgets corresponding to this row.
            .map((_, i) => getDashboardGridRow(i))
            // Remove empty rows from the dashboard grid.
            .filter(row => row.length > 0);

        // Fix widget indexes to correspond current dashboard distribution.
        this.refreshWidgetIndexes();

        // Refresh dashboard preview.
        this.refreshDashboardPreview();
    }

    private refreshWidgetIndexes(): void {
        if (!this.dashboardGrid) return;

        // Fix widget indexes to correspond current dashboard distribution.
        this.dashboardGrid.forEach((row, i) =>
            row.forEach((widget, j) => {
                widget.rowIndex = i;
                widget.columnIndex = j;
            }));
    }

    private refreshWidgetSourceSettings(): void {
        if (!this.widgetSources || !this.dashboardWidgets) return;

        // Set widget source options, avoiding repetitions with existent ones.
        const widgetSources: IWidgetSource[] = this.widgetSources.filter(source =>
            !this.dashboardWidgets.some(widget => widget.widgetSourceId === source.id));

        // Instance widget source dropdown options.
        this.widgetSourceOptions = widgetSources.map(widget => <SelectItem>{ label: widget.name, value: widget });
    }

    private refreshDashboardQueriesGrid(): void {
        this.queriesComponent?.refreshDashboardQueriesGrid();
    }

    protected refreshDashboardPreview(): void {
        const dashboardWidgets: IDashboardWidgetView[] = this.dashboardWidgets
            .map(widget => <IDashboardWidgetView>{
                id: widget.id,
                rowIndex: widget.rowIndex,
                columnIndex: widget.columnIndex,
                widgetSourceId: widget.widgetSourceId,
                widgetType: widget.widgetSource?.widgetType
            });

        this.dashboardPreview = {
            ...this.dashboard,

            name: this.dashboardName,
            displayName: this.displayName,

            pdfHeaderHtmlCode: this.headerHtmlCode,
            pdfHeaderCssStyle: this.headerCssStyle,
            pdfFooterHtmlCode: this.footerHtmlCode,
            pdfFooterCssStyle: this.footerCssStyle,

            widgets: dashboardWidgets,

            layout: this.layoutStyle,
            style: this.dashboardStyles,
            widgetsMargin: this.widgetsMargin,
            widgetsPadding: this.widgetsPadding
        };
    }

    protected refreshDashboardStylesSettings(): void {
        const padding: [number, string] = this.getCssPropertyValue("padding");
        const margin: [number, string] = this.getCssPropertyValue("margin");

        if (!!padding) {
            this.widgetsPadding = padding[0];
            this.widgetsPaddingUnit = padding[1];
        }

        if (!!margin) {
            this.widgetsMargin = margin[0];
            this.widgetsMarginUnit = margin[1];
        }

        this.refreshDashboardStyles();
    }

    protected refreshDashboardStyles(): void {
        // Remove non-ascii characters from the string.
        this.dashboardStyles = this.dashboardStyles.replace(/[^\x20-\x7E\n\t]/g, "");

        this.htmlRenderingService.insertScopedStyles(this.cssInjectionScopeId, this.elementRef.nativeElement, this.dashboardStyles);
    }

    // CRUD methods.
    protected getSource(widgetSourceId: string): IWidgetSource {
        if (!widgetSourceId) return null;

        return this.widgetSources.find(widget => widget.id === widgetSourceId);
    }

    protected addWidget(source: IWidgetSource): void {
        if (!source) return;

        // Create new dashboard widget.
        const row: number = this.rowIndex ?? this.dashboardGrid.length;
        const column: number = this.columnIndex ?? 0;
        const widget: IDashboardWidgetBuild = {
            id: null,
            widgetSourceId: source.id,
            widgetSource: source,
            columnIndex: column,
            rowIndex: row
        };

        // Add this widget to the dashboard widgets.
        this.dashboardWidgets.push(widget);

        // Insert the widget in the correct position inside the grid.
        if (this.columnIndex != null) {
            this.dashboardGrid[row].splice(column, 0, widget);

        } else this.dashboardGrid.splice(row, 0, [widget]);

        // If this widget is not registered, add it to the list.
        if (!this.widgetSources.some(widgetSource => widgetSource.id === source.id)) {
            this.widgetSources.push(source);
        }

        // Refresh the grid.
        this.refreshWidgetIndexes();
        this.refreshDashboardPreview();
        this.refreshWidgetSourceSettings();
        this.refreshDashboardQueriesGrid();
        this.clean();
    }

    protected editWidget(source: IWidgetSource): void {
        if (!source) return;

        this.widgetImport = false;
        this.widgetSource = source;
    }

    protected refreshWidget(source: IWidgetSource): void {
        if (!source) return;

        // Change the widget source id in all the dashboard widgets using this widget source and refresh the dashboard grid.
        this.dashboardWidgets.forEach((widget, i) => {
            if (widget.widgetSourceId !== this.widgetSource?.id) return;
            widget = { ...widget, widgetSource: source, widgetSourceId: source.id };
            this.dashboardGrid[widget.rowIndex][widget.columnIndex] = widget;
            this.dashboardWidgets[i] = widget;
        });

        // If the widget source id changed substitute the old widget source by this one, in the list of widget sources.
        if (source.id !== this.widgetSource?.id) {
            const index: number = this.widgetSources.findIndex(widget => widget.id === this.widgetSource?.id);
            const deleteCount: number = index < 0 ? 0 : 1;
            this.widgetSources.splice(index, deleteCount, source);
        }

        // Refresh the grid.
        this.refreshDashboardPreview();
        this.clean();
    }

    protected deleteWidget(source: IWidgetSource): void {
        if (!source) return;

        const index: number = this.dashboardWidgets
            .findIndex(widget => widget.widgetSourceId === source.id);

        if (index < 0) return;

        this.confirmationService.confirm({
            message: `Are you sure that you want to delete widget${source.name?.length ? ` "${source.name}"` : ""}?`,
            accept: (): void => {
                const widget: IDashboardWidgetBuild = this.dashboardWidgets[index];

                // Remove the widget corresponding to this source and refresh dashboard.
                this.dashboardWidgets.splice(index, 1);
                this.dashboardGrid[widget.rowIndex].splice(widget.columnIndex, 1);

                // Remove the row if it becomes empty.
                if (this.dashboardGrid[widget.rowIndex].length === 0) {
                    this.dashboardGrid.splice(widget.rowIndex, 1);
                }

                // If the widget is local, remove it also from widgets list.
                if (!source.name?.length) {
                    this.widgetSources = this.widgetSources.filter(widget => widget.id !== source.id);
                }

                this.refreshWidgetIndexes();
                this.refreshDashboardPreview();
                this.refreshWidgetSourceSettings();
                this.refreshDashboardQueriesGrid();
                this.clean();
            }
        })
    }

    protected clean(): void {
        this.rowIndex = null;
        this.columnIndex = null;
        this.widgetSource = null;
        this.widgetImport = null;
    }

    protected save(): void {
        this.dashboardService.update(this.dashboardId, this.dashboardBuild)
            .then(_ => {
                this.hasForcedPermitToLeave = true;
                this.queriesComponent.save().then();

                // A standard scenario for how the client-side app knows about which page routes are allowed for
                // Current user is based on user login event - on login we refresh routes from the server.
                // For the reporting feature this means that when the report editor user creates a new report
                // (which has new URL slug) then on the further logins, all end-users will get actual new report routes.
                // But an exceptional case is the report-editor user himself being already logged in and working with reports.
                // E.g.scenario: he's just change URL slug of a report and right after that he wants to open the report
                // View page. Therefore, we need to refresh the routes by doing this:
                this.securityService.refreshRoutes(true).then();

                this.router.navigate(["/app/reporting3/dashboards"]).then();
            });
    }

    protected cancel(): void {
        const cancelFunc = () => {
            this.hasForcedPermitToLeave = true;
            this.queriesComponent.cancel().then();
            return this.router.navigate(["/app/reporting3/dashboards"]);
        }

        if (this.dirty) {
            this.confirmationService.confirm({
                accept: cancelFunc,
                message: "Are you sure that you want to discard the changes made?",
            })
        } else cancelFunc().then();
    }

    // Overly menu methods.
    protected showOverlyMenu(event: Event, row: number, column?: number): void {
        if (!event?.target) return;
        const target: HTMLElement = event.target as HTMLElement;
        const placeholder: HTMLButtonElement = target.querySelector(".widget-menu-placeholder");

        // If there is a declared placeholder for the menu, show the menu from the placeholder.
        if (!!placeholder) {
            this.menu.onShow.pipe(take(1)).subscribe(_ => {
                this.menu.container.style.marginLeft = `${placeholder.offsetLeft}px`;
            });
        }

        this.rowIndex = row;
        this.columnIndex = column;
        this.menu.toggle(event);
        event.stopPropagation();
    }

    // Dragging methods.
    protected draggable(widget: IDashboardWidgetBuild): boolean {
        return !!this.draggableWidget && this.draggableWidget.widgetSourceId === widget.widgetSourceId;
    }

    protected shrinkable(widget: IDashboardWidgetBuild): boolean {
        return !!this.shrinkableWidget && this.shrinkableWidget.widgetSourceId === widget.widgetSourceId;
    }

    protected droppable(row: number, column?: number): boolean {
        if (!this.draggableWidget) return false;

        const currentRow: number = this.draggableWidget.rowIndex;
        const currentColumn: number = this.draggableWidget.columnIndex;
        const horizontalAdjacency: boolean = row - currentRow === 0 || row - currentRow === 1;
        const verticalAdjacency: boolean = column - currentColumn === 0 || column - currentColumn === 1;

        // No horizontal zones adjacent to this widget is valid for drop if the row of this widget only contains this one.
        return !(column == null && horizontalAdjacency && this.dashboardGrid[currentRow].length === 1)
            // No vertical zones adjacent to this widget is valid for drop.
            && !(column != null && verticalAdjacency && row === currentRow);
    }

    protected expandable(row: number, column?: number): boolean {
        return !!this.shrinkableWidget
            && this.shrinkableWidget.rowIndex === row
            && this.shrinkableWidget.columnIndex === column - 1;
    }

    protected dragWidget(widget: IDashboardWidgetBuild): void {
        this.draggableWidget = widget;
    }

    protected shrinkWidget(widget: IDashboardWidgetBuild): void {
        // If there is no draggable widget, don't shrink.
        if (!this.draggableWidget) {
            this.shrinkableWidget = null;
            return;
        }

        // If the draggable widget is adjacent to this one, don't shrink.
        if (this.draggableWidget.rowIndex === widget.rowIndex && (
            this.draggableWidget.columnIndex === widget.columnIndex ||
            this.draggableWidget.columnIndex === widget.columnIndex + 1)) {
            this.shrinkableWidget = null;
            return;
        }

        // Otherwise, shrink this widget.
        this.shrinkableWidget = widget;
    }

    protected dropWidget(): void {
        this.draggableWidget = null;
        this.shrinkableWidget = null;
    }

    protected moveWidget(row: number, column?: number, relative: boolean = false): void {
        if (!this.draggableWidget) return;

        let currentRow: number = this.draggableWidget.rowIndex;
        let currentColumn: number = this.draggableWidget.columnIndex;
        const singleWidget: boolean = this.dashboardGrid[currentRow].length === 1;

        // If new coordinates are relative to current position, then make them absolute.
        if (relative) {
            // Make row position absolute.
            row += currentRow;

            // If widget change its row and is not the only widget in this row.
            if (currentRow != row && !singleWidget) {
                // Delete column to indicate that the widget will take up a new row.
                column = null;
                // If the new row is before this one, increase the row to shift the rows accordingly.
                if (row < currentRow) row++;
            }

            // If widget change its column.
            if (column != null) {
                // Make column position absolute.
                column += currentColumn;
                // If the new column is after this one, increase the column to shift the columns accordingly.
                if (currentColumn < column) column++;
            }
        }

        if (column == null) {
            // If the dropping zone is a horizontal zone, add a new row with this widget.
            this.dashboardGrid.splice(row, 0, [this.draggableWidget]);
            if (row <= currentRow) currentRow++;
        } else {
            // If the dropping zone is a vertical zone, add this widget into the corresponding column of the row.
            this.dashboardGrid[row].splice(column, 0, this.draggableWidget);
            if (row === currentRow && column <= currentColumn) currentColumn++;
        }

        // Remove the widget from its old position.
        this.dashboardGrid[currentRow].splice(currentColumn, 1);

        // Delete the old row of the widget if it became empty.
        if (singleWidget) this.dashboardGrid.splice(currentRow, 1);

        // Fix widget indexes to correspond current dashboard distribution.
        this.refreshWidgetIndexes();

        // Refresh dashboard preview.
        this.refreshDashboardPreview();

        // End dragging.
        this.dragEnd();
    }

    protected drag(event: any): void {
        this.dragging = true;
        let position: number;
        const step: number = 30;
        const layout: HTMLElement = document.querySelector("html");

        if (!layout) return;

        // Restore scroll of the screen in widget dragging only when its necessary.
        if (window.innerHeight * 0.8 < event.clientY) position = layout.scrollTop + step;
        if (window.innerHeight * 0.2 > event.clientY) position = layout.scrollTop - step;
        if (position != null) layout.scrollTo({ top: position, behavior: "auto" });
    }

    protected dragEnd(): void {
        this.dragging = false;
        this.shrinkableWidget = null;
    }

    // Styling methods.
    protected widgetStyle(widget: IDashboardWidgetBuild): string {
        if (!this.dashboardGrid) return "";

        const row: IDashboardWidgetBuild[] = this.dashboardGrid[widget.rowIndex];
        if (!row?.length) return "";

        //const padding: number = this.widgetsPadding ?? 0;
        const margin: number = this.widgetsMargin ?? 0;
        const width: number = 100 / row.length;
        const space: number = this.shrinkable(widget) ? 300 : margin * 2;

        return `width: calc(${width}% - ${space}px);`;
    }

    protected widgetClass(widget: IDashboardWidgetBuild): string {
        let styleClass: string = "dashboard-widget";

        if (!this.draggableWidget) styleClass += " dashboard-widget-static";
        else if (this.draggable(widget)) styleClass += " dashboard-widget-draggable"

        return styleClass;
    }

    protected widgetButtonsClass(widgetContainer: HTMLDivElement): string {
        let styleClass: string = "widget-buttons";

        // If the widget container cannot contain the buttons vertically or horizontally,
        // shrink the button container.
        if (widgetContainer.offsetWidth < 350 && widgetContainer.offsetHeight < 350) {
            styleClass += " shrink";
        }

        // If the widget container cannot contain the buttons horizontally, place them vertically.
        // It also applies when the container is shrunk and have more height than width.
        if (widgetContainer.offsetWidth < 350 && widgetContainer.offsetWidth < widgetContainer.offsetHeight) {
            styleClass += " vertical";

            // Otherwise, place the buttons horizontally.
        } else styleClass += " horizontal";

        return styleClass;
    }

    protected separatorClass(row: number, column?: number): string {
        let styleClass: string = column == null
            ? "dashboard-widget-row-separator"
            : "dashboard-widget-separator";

        if (this.droppable(row, column)) {
            if (!this.dragging) styleClass += " dashboard-widget-drop"
            if (this.expandable(row, column)) styleClass += " dashboard-widget-separator-grow";
        }

        return styleClass;
    }

    // Auxiliary methods.
    private getCtrlEnterKeyboardHandler(): KeyBinding {
        return {
            key: "Mod-Enter",
            run: (): boolean => {
                // Update the styles preview.
                this.refreshDashboardStylesSettings()

                // Capture this key (return true to stop bubbling).
                return true;
            }
        }
    }

    private getDefaultStyles(): string {
        const defaultStyles: string = this.htmlRenderingService.getDefaultCssRules(".dashboard-default-styles");

        if (!defaultStyles) return;

        // Patterns to format the code to CSS code style.
        const formatRules: { pattern: RegExp, replacement: string }[] = [
            // Puts a new line and a tab after all opening brackets (inits code block).
            { pattern: /{/g, replacement: "{\n" },
            // Puts two new lines after all closing brackets (closes code block).
            { pattern: /}/g, replacement: "}\n\n" },
            // Puts a new line after all semicolons.
            { pattern: /;/g, replacement: ";\n" },
            // Puts a tab after all new lines preceded by open brackets,
            // except if the code block is empty (indents code block).
            { pattern: /{\n(?!\s*})/g, replacement: "{\n\t" },
            // Puts a tab after all new lines preceded by semicolons,
            // except for the last one inside a code block (indents code block).
            { pattern: /;\n(?!\s*})/g, replacement: ";\n\t" },
        ];

        // Comments to clarify the code.
        const commentRules: { pattern: RegExp, insertion: string }[] = [
            {
                pattern: /^\s*\.dashboard-widget\s*{[^{}]+}/g,
                insertion: "/* Styles of the dashboard cells (widget containers). */\n"
            },
            {
                pattern: /^\s*\.dashboard-widget-row\s*{[^{}]+}/g,
                insertion: "/* Styles of the dashboard rows. */\n"
            },
            {
                pattern: /^\s*\.dashboard-widget-separator\s*{[^{}]+}/g,
                insertion: "/* Styles of the vertical separators between dashboard cells. */\n"
            },
            {
                pattern: /^\s*\.dashboard-widget-row-separator\s*{[^{}]+}/g,
                insertion: "/* Styles of the horizontal separators between dashboard rows. */\n"
            },
            {
                pattern: /^\s*\.dashboard-widget-grid.cards\s[\s\S]*{[^{}]+}/g,
                insertion: "/* Styles of the 'cards' dashboard layout. \n " +
                    "These only have effect if 'cards' layout is set. */\n"
            },
            {
                pattern: /^\s*\.dashboard-widget-grid.dividers\s[\s\S]*{[^{}]+}/g,
                insertion: "/* Styles of the 'dividers' dashboard layout. \n " +
                    "These only have effect if 'dividers' layout is set. */\n"
            },
            {
                pattern: /^\s*\.nhs-refresh-button-container\s[\s\S]*{[^{}]+}/g,
                insertion: "/* Styles of the container of the 'Refresh Data' button. \n " +
                    "You can use it to control the position of the button. \n" +
                    "These only have effect if 'Show manual refresh button' checkbox is ticked. */\n"
            },
            {
                pattern: /^\s*\.nhs-refresh-button\s[\s\S]*{[^{}]+}/g,
                insertion: "/* Styles of the 'Refresh Data' button. \n " +
                    "These only have effect if 'Show manual refresh button' checkbox is ticked. */\n"
            },
        ];

        // Apply all replacement rules to clean the CSS code.
        const formattedStyles: string = formatRules
            .reduce((code: string, replacementRule: { pattern: RegExp, replacement: string }) =>
                code.replace(replacementRule.pattern, replacementRule.replacement), defaultStyles);

        // Apply comments to the CSS code blocks.
        const commentedStyles: string = commentRules
            .reduce((code: string, replacementRule: { pattern: RegExp, insertion: string }) =>
                code.replace(replacementRule.pattern, match => replacementRule.insertion + match), formattedStyles);

        const widgetCodeNote = "/* Edit dashboard css styles here. */ \n\n" +
            "/* You can use the widget-code of a existing widget as selector class for declaring css rules.\n" +
            "For example if the code of a widget is 'invoices-2024', you can do:\n" +
            ".widget-invoices-2024 {\n\t Your styles here \n}\n*/\n\n";

        return widgetCodeNote + commentedStyles;
    }

    private getWidgetCssRuleMatches(): RegExpMatchArray[] {
        // Search for widget CSS blocks that are not inside @ blocks.
        const selector: string = `.${this.dashboardAuxiliaryService.widgetClass}`;

        const rulePatternString: string = this.htmlRenderingService.getCssRulePattern(selector);

        const rulePattern: RegExp = new RegExp(rulePatternString, "g");

        const ruleMatches: RegExpMatchArray[] = Array.from(this.dashboardStyles.matchAll(rulePattern));

        return ruleMatches.filter((ruleMatch: RegExpMatchArray): boolean => {
            // Check if this block is inside an @ block.
            const precedent: string = this.dashboardStyles.slice(0, ruleMatch.index);

            const lastAtBlockOpen: number = precedent.lastIndexOf("@");

            // This block is not inside an @ block if there is no @ selector before.
            if (lastAtBlockOpen === -1) return true;

            const blockPrecedent: string = precedent.slice(lastAtBlockOpen);

            const openingBraces: number = blockPrecedent.split("{").length - 1;
            const closingBraces: number = blockPrecedent.split("}").length - 1;

            // This block is not inside an @ block only if there are less
            // or equal opening than closing braces from the last @ selector.
            return openingBraces <= closingBraces;
        });
    }

    private getCssPropertyValue(property: string): [value: number, unit: string] {
        // Search for the string <property>: <value><unit> inside widget CSS blocks.
        const propertyPatternString: string = this.htmlRenderingService.getCssDimensionPropertyPattern(property);

        const propertyPattern: RegExp = new RegExp(propertyPatternString, "g");

        // Match all blocks with the widget selector which are not inside @ blocks.
        // Reverse the matches as CSS considers the last rule as the relevant one if there are many equal ones.
        const ruleMatches: RegExpMatchArray[] = this.getWidgetCssRuleMatches().reverse();

        // Return the value and unit of the property in their first valid occurrence.
        for (const ruleMatch of ruleMatches) {
            // Match all properties with the given property name.
            const propertyMatches: RegExpMatchArray[] = Array.from(ruleMatch[0].matchAll(propertyPattern));

            for (const propertyMatch of propertyMatches) {
                // Get the value and the unit of the CSS dimension property.
                const value: number = parseFloat(propertyMatch?.groups?.["value"]);
                const unit: string = propertyMatch?.groups?.["unit"] ?? "px";

                // If the value is valid, return value and unit.
                if (!isNaN(value)) return [value, unit];
            }
        }

        return null;
    }

    private replaceCssPropertyValue(property: string, value: number, unit: string): void {
        // Search for the string <property>: <value><unit> inside widget CSS blocks.
        const propertyPatternString: string = this.htmlRenderingService.getCssDimensionPropertyPattern(property);

        const propertyPattern: RegExp = new RegExp(propertyPatternString, "g");

        // Match all widget CSS blocks which are not inside @ blocks.
        // Reverse the matches to not affect the relative position of the remaining ones after a replacement.
        const ruleMatches: RegExpMatchArray[] = this.getWidgetCssRuleMatches().reverse();

        if (ruleMatches.some((ruleMatch: RegExpMatchArray): boolean => !!ruleMatch[0].match(propertyPattern))) {
            // If there are widget CSS blocks with the given property.

            this.dashboardStyles = ruleMatches.reduce((styles: string, ruleMatch: RegExpMatchArray): string => {
                // Replace all occurrences of values of the target property with the given value.
                const replacement: string = `${property}: ${value}${unit}`;
                const ruleContent: string = ruleMatch[0].replace(propertyPattern, replacement);

                // Avoid replacement if there were no property matches.
                if (ruleContent === ruleMatch[0]) return styles;

                const prefix: string = styles.slice(0, ruleMatch.index);
                const suffix: string = styles.slice(ruleMatch.index + ruleMatch[0].length);

                return prefix + ruleContent + suffix;

            }, this.dashboardStyles);

        } else {
            // Otherwise, add a new widget CSS block with <property>: <value><unit>.
            const selector: string = `.${this.dashboardAuxiliaryService.widgetClass}`;

            const widgetCssBlock: string = `\n${selector} {\n\t${property}: ${value}${unit};\n}\n`;

            this.dashboardStyles = this.dashboardStyles + widgetCssBlock;
        }
    }

    protected onDashboardCodeFocus(): void {
        this.presetDashboardCode();
    }

    protected presetDashboardCode(): void {
        if (!this.dashboardCode && !!this.dashboardName) {
            this.dashboardCode = this.dashboardName.trim().replace(/\W+/g, "-");
        }
    }

    protected onMarginChange(): void {
        this.replaceCssPropertyValue("margin", this.widgetsMargin, this.widgetsMarginUnit);
        this.refreshDashboardStyles();
    }

    protected onPaddingChange(): void {
        this.replaceCssPropertyValue("padding", this.widgetsPadding, this.widgetsPaddingUnit);
        this.refreshDashboardPreview();
        this.refreshDashboardStyles();
    }

    protected onLayoutChange(event: DropdownChangeEvent): void {
        this.widgetsMargin = event.value === LayoutType.Cards ? 20 : 0;
        this.replaceCssPropertyValue("margin", this.widgetsMargin, this.widgetsMarginUnit);
        this.refreshDashboardPreview();
        this.refreshDashboardStyles();
    }

    protected resetLayoutToDefaults(): void {
        this.layoutStyle = LayoutType.Dividers;
        this.widgetsPadding = 15;
        this.widgetsMargin = 0;
        this.replaceCssPropertyValue("padding", this.widgetsPadding, this.widgetsPaddingUnit);
        this.replaceCssPropertyValue("margin", this.widgetsMargin, this.widgetsMarginUnit);
        this.refreshDashboardPreview();
        this.refreshDashboardStyles();
    }

    protected showCssEditor(): void {
        this.dashboardStylesBackup = this.dashboardStyles;
        this.editStylesDialogVisible = true;
    }

    protected onCssEditorSave(): void {
        this.dashboardStylesBackup = null;
        this.editStylesDialogVisible = false;
        this.refreshDashboardStylesSettings();
    }

    protected onCssEditorCancel(): void {
        if (!this.dashboardStylesBackup) return;
        this.dashboardStyles = this.dashboardStylesBackup;
        this.editStylesDialogVisible = false;
        this.refreshDashboardStylesSettings();
    }

    protected refreshLoadingMessageHTMLPreview(): void {
        this.loadingMessageHtmlPreview = {
            id: null,
            htmlCode: this.loadingMessageHtmlCode ?? "",
            cssRules: this.loadingMessageCssRules ?? "",
            widgetSource: null,
            widgetSourceId: null
        };
    }

    protected refreshErrorMessageHTMLPreview(): void {
        this.errorHandlingHtmlPreview = {
            id: null,
            htmlCode: this.errorHandlingHtmlCode ?? "",
            cssRules: this.errorHandlingCssRules ?? "",
            widgetSource: null,
            widgetSourceId: null
        };
    }
}